import React from 'react';
import { DateTime } from 'luxon';

import Table from '../../components/table';
import {
  parseLuxonDateToParts,
  defaultParsedTime,
  emptyDateFieldCharacter,
} from '../../lib/date';
import { toTitleCase } from '../../lib/strings';
import { EventTimelineIrrOp } from './EventTimelineIrrOp';
import { Update } from '../../types/flight';

const noRowsValue = 'No Events';
const eventKeys = [
  'Actual Gate Arrival',
  'Actual Gate Departure',
  'Actual Runway Arrival',
  'Estimated Gate Arrival',
  'Scheduled Gate Arrival',
  'Actual Runway Departure',
  'Estimated Runway Arrival',
  'Estimated Gate Departure',
  'Scheduled Gate Departure',
  'Scheduled Runway Arrival',
  'Estimated Runway Departure',
  'Scheduled Runway Departure',
  'Scheduled Equipment',
  'Actual Equipment',
  'Departure Terminal',
  'Arrival Terminal',
  'Arrival Gate',
  'Departure Gate',
  'Baggage',
  'Tail Number',
  'Status',
  'Redirected/diverted Airport',
];

/**
 * Parses a change value.
 * @param {string | DateTime} change - The change value.
 * @returns {string} The parsed change value.
 */
const parseChange = (change: string | DateTime): string => {
  if (change instanceof DateTime) {
    const oldDate = parseLuxonDateToParts(change);
    return `${oldDate.displayDateFormat} ${oldDate.time12Hr} ${oldDate.dayPeriod}`;
  }
  return change;
};

interface EventTimelineFormProps {
  eventUpdates: Update[];
  departureAirportDisplayCode?: string;
  arrivalAirportDisplayCode?: string;
  departureTimeZone?: string;
  arrivalTimeZone?: string;
}

/**
 * EventTimeline component to display the event timeline.
 * @param {EventTimelineFormProps} props - The props for the component.
 * @returns {JSX.Element} The EventTimeline component.
 */
export const EventTimeline: React.FC<EventTimelineFormProps> = ({
  eventUpdates,
  departureAirportDisplayCode,
  arrivalAirportDisplayCode,
  departureTimeZone,
  arrivalTimeZone,
}) => {
  const headers = [
    { header: 'Update Time (UTC)' },
    {
      header: `Departure (${departureAirportDisplayCode || emptyDateFieldCharacter})`,
    },
    {
      header: `Arrival (${arrivalAirportDisplayCode || emptyDateFieldCharacter})`,
    },
    { header: 'Data Updated' },
  ];

  // Update the rows with user-friendly values
  const rowData = eventUpdates?.reduce<{ key: string; values: (JSX.Element | string[])[] }[]>((accumulator, update, index) => {
    const date = update?.metaData?.date;

    // Create list of all the changes
    const changes = [];
    for (const key in update?.changes) {
      const change = update?.changes?.[key];
      if (!change) continue;

      const updatedKey = toTitleCase(key);

      // Only certain updates need to be included
      if (!eventKeys.includes(updatedKey)) continue;

      const oldChange = change.old ? parseChange(change.old) : '';
      const newChange = change.new ? parseChange(change.new) : '';

      if (oldChange && newChange) {
        changes.push(`${updatedKey} changed from ${oldChange} to ${newChange}`);
      } else if (oldChange) {
        changes.push(`${updatedKey} of ${oldChange} removed`);
      } else if (newChange) {
        changes.push(`${updatedKey} changed to ${newChange}`);
      }
    }

    const parsedDate = date ? parseLuxonDateToParts(date) : defaultParsedTime;
    const parsedDepartureDate =
      (departureTimeZone && date && parseLuxonDateToParts(date.setZone(departureTimeZone))) ||
      defaultParsedTime; // these defaults shouldn't be used, but they keep TypeScript happy
    const parsedArrivalDate =
      (arrivalTimeZone && date && parseLuxonDateToParts(date.setZone(arrivalTimeZone))) ||
      defaultParsedTime; // these defaults shouldn't be used, but they keep TypeScript happy

    const dateColumns = [
      (parsedDate && (
        <>
          {parsedDate?.time24Hr}
          <br />
          {parsedDate.displayDateFormat}
        </>
      )) || emptyDateFieldCharacter,
      (parsedDepartureDate && (
        <>
          {`${parsedDepartureDate.time12Hr} ${parsedDepartureDate.dayPeriod} ${parsedDepartureDate.timeZoneOffset}`}
          <br />
          {parsedDepartureDate.displayDateFormat}
        </>
      )) || emptyDateFieldCharacter,
      (parsedArrivalDate && (
        <>
          {`${parsedArrivalDate.time12Hr} ${parsedArrivalDate.dayPeriod} ${parsedArrivalDate.timeZoneOffset}`}
          <br />
          {parsedArrivalDate.displayDateFormat}
        </>
      )) || emptyDateFieldCharacter,
    ];

    if (date && changes.length) {
      accumulator.push({
        key: `changes${index}${date}`,
        values: [...dateColumns, changes],
      });
    }

    // Since the update list is reversed before display, if this is pushed after the changes,
    // it will appear before them
    if (date && update?.irregular?.type && update.irregular.type !== 'POSITIONAL_FOR') {
      const irregularOp = update.irregular;
      accumulator.push({
        key: `irregular${index}${date}`,
        values: [
          ...dateColumns,
          <EventTimelineIrrOp key={`irregularCell${index}${date}`} irregularOp={irregularOp} />,
        ],
      });
    }

    return accumulator;
  }, []);

  return (
    <>
      <h1>Event Timeline</h1>
      <Table headers={headers} data={rowData.reverse()} noRowsValue={noRowsValue} multiLine={true} />
    </>
  );
};